import { useContext, useEffect, useState, useCallback } from "react";  // Use useCallback to memoize the function
import { useForm } from "react-hook-form";
import ApkServices from "services/ApkServices";
import { SidebarContext } from "context/SidebarContext";
import { toast } from "react-toastify";  // Importing toast from react-toastify

const useApkSubmit = (id) => {
  const { isDrawerOpen, closeDrawer, setIsUpdate } = useContext(SidebarContext);
  const [loading, setLoading] = useState(false);
  const [apkUrl, setApkUrl] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [apkType, setApkType] = useState("");

  const {
    register,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors },
    clearErrors
  } = useForm();

  // Function to fetch APK data for editing
  const getApkData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await ApkServices.getApkById(id);
        const data = response.data;
        setValue("version", data.version || "");
        setValue("type", data.type || "");
        setValue("release_notes", data.release_notes || "");
        setValue("mandatory", !!data.is_mandatory);
        setApkUrl(data.download_url || "");
        setIsMandatory(!!data.is_mandatory);
        setApkType(data.type || "");
      } catch (error) {
       // console.error("Error fetching APK data:", error);
        toast.error(error?.response?.data?.message || "Error fetching APK data", {
          position: toast.POSITION.TOP_CENTER // Toast position
        });
      } finally {
        setLoading(false);
      }
    }
  }, [id, setValue]);  // Using useCallback to memoize and include `id` and `setValue` in dependencies

  useEffect(() => {
    if (isDrawerOpen && id) {
      getApkData();  // Fetch APK data if editing
    } else {
      // Reset fields when not in edit mode or drawer is closed
      setValue("version", "");
      setValue("type", "");
      setValue("release_notes", "");
      setValue("mandatory", false);
      setApkUrl("");
      setIsMandatory(false);
      setApkType("");
      clearErrors();
    }
  }, [id, isDrawerOpen, setValue, clearErrors, getApkData]); // Add getApkData in the dependency array

  // Handle form submission
  const onSubmit = async (formValues) => {
    // Log the form values before submission
  
    if (!formValues.version || !formValues.type || !formValues.release_notes || !apkUrl) {
      // Trigger errors for empty fields
      if (!formValues.version) trigger("version");
      if (!formValues.type) trigger("type");
      if (!formValues.release_notes) trigger("release_notes");
      if (!apkUrl) {
        toast.error("Please select an APK file to upload!", {
          position: toast.POSITION.TOP_CENTER // Toast position
        });
      }
      return;
    }

    const payload = {
      version: formValues.version,
      type: formValues.type,
      release_notes: formValues.release_notes,
      download_url: apkUrl,
      is_mandatory: isMandatory,
    };

    try {
      setLoading(true);
      await ApkServices.addApk(payload); // Update APK
      setIsUpdate(true);  // Indicate that the update was successful
      toast.success("APK successfully updated!", {
        position: toast.POSITION.TOP_CENTER // Toast position
      });
      closeDrawer();  // Close the drawer after successful update
    } catch (error) {
    //  console.error("Error updating APK", error);
      toast.error(error?.response?.data?.message || "Error updating APK. Please try again.", {
        position: toast.POSITION.TOP_CENTER // Toast position
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    register,
    trigger,
    handleSubmit,
    onSubmit,
    loading,
    errors,
    apkUrl,
    setApkUrl,
    isMandatory,
    setIsMandatory,
    apkType,
    setApkType,
  };
};

export default useApkSubmit;
